import React, {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {
  ProjectConfigType,
  ProjectPayloadType,
  ProjectPushPayloadType,
  ProjectType
} from "../types/projects.types";
import {usePagination} from "./usePagination";

type ProjectsFetchMoreMethod = (refresh?: boolean) => Promise<void>;
type ProjectsRefreshMethod = () => Promise<ProjectType[]>;

interface UseProjectsReturn {
  projects: ProjectType[];
  fetched: boolean;
  hasMore: boolean;
  currentPage: number;
  fetchMore: ProjectsFetchMoreMethod;
  refresh: ProjectsRefreshMethod;
}

export const useProjects = (
  params: {[key: string]: any} = {}
): UseProjectsReturn => {
  const {getProjects} = useApi();
  const [projects, setProjects] = useState<ProjectType[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const {appendNextPage, refreshLastPage, checkHasMore, currentPage} =
    usePagination<ProjectType>(setProjects, setHasMore, hasMore, params?.page, params?.limit);

  const [fetched, setFetched] = useState<boolean>(false);

  const fetchProjects: ProjectsRefreshMethod = async() => {
    try {
      setFetched(false);
      const projects = checkHasMore<ProjectType>(await withCache(getProjects, [params], 60_000));
      setProjects(projects);
      return projects;
    } catch (error) {
      console.error("Error fetching projects", error);
      throw new Error("Error fetching projects", {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const fetchMore: ProjectsFetchMoreMethod = async refresh => {
    try {
      setFetched(false);
      if(refresh)
        await refreshLastPage(async appendParams => getProjects({...params, ...appendParams}));
      else {
        await appendNextPage(async appendParams =>
          await withCache(getProjects, [{...params, ...appendParams}], 60_000));
      }

    } catch (error) {
      console.error("Error fetching more projects", error);
    } finally {
      setFetched(true);
    }
  };


  useEffect(() => {
    fetchProjects();
  }, [params?.page]);

  return {
    currentPage,
    fetchMore,
    fetched,
    hasMore,
    projects,
    refresh: fetchProjects
  };
};


type ProjectPushMethod =
  (params?: Partial<ProjectPayloadType & {newThumbnailFile: File}>) => Promise<ProjectType>;
type ProjectPushThumbnailMethod = (file?: File | null) => Promise<void>;
type ProjectPushNewOwnerMethod = (username?: string) => Promise<void>;
type ProjectUpdateConfigMethod = (config?: Partial<ProjectConfigType> | null) => Promise<void>;
type ProjectFetchMethod = (quiet?: boolean ) => Promise<ProjectType>;

interface UseProjectReturn extends Partial<ProjectType> {
  project: ProjectType | null;
  fetched: boolean;
  push: ProjectPushMethod;
  pushThumbnail: ProjectPushThumbnailMethod;
  pushNewOwner: ProjectPushNewOwnerMethod;
  updateConfig: ProjectUpdateConfigMethod;
  setConfig: React.Dispatch<React.SetStateAction<ProjectConfigType | null>>;
  setThumbnail: (file: File) => void;
  refresh: ProjectFetchMethod;
}

export const useProject = (id?: string): UseProjectReturn => {
  const {createProject, getProject, updateProject, updateProjectConfig, updateProjectThumbnail} = useApi();

  let projectId = id;
  const [project, setProject] = useState<ProjectType | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [config, setConfig] = useState<ProjectConfigType | null>(null);

  const updateConfig: ProjectUpdateConfigMethod = async(
    configData = config
  ) => {
    try {
      if(!configData) throw new Error("No config to update");

      await updateProjectConfig(configData, projectId);
      clearCache("getProject", [projectId]);
    } catch (error) {
      console.error("Error updating config", error);
      throw new Error(`Error updating config from project '${projectId}'`, {cause: error});
    }
  };

  const pushThumbnail: ProjectPushThumbnailMethod = async(file = thumbnail) => {
    if(!file || typeof file === "string") return;
    try {
      await updateProjectThumbnail(file, projectId);
      clearCache("getProject", [projectId]);
    } catch (error) {
      console.error("Error updating thumbnail", error);
      throw new Error(`Error updating thumbnail from project '${projectId}'`, {cause: error});
    }
  };

  const pushNewOwner: ProjectPushNewOwnerMethod = async username => {
    try {
      const {ownerId} = await updateProject({
        ownerUsername: username
      });

      if(ownerId) setProject({...project, ownerId} as ProjectType);

      clearCache("getProject", [projectId]);
    } catch (error) {
      console.error("Error updating owner", error);
      throw new Error(`Error updating owner from project '${projectId}'`, {cause: error});
    }
  };

  const fetchProject: ProjectFetchMethod = async(quiet = false) => {
    setFetched(quiet);
    try {

      const project = await withCache(getProject, [projectId], 60_000);
      setProject(project);
      return project;
    } catch (error) {
      console.error("Error fetching project", projectId, error);
      throw new Error(`Error fetching project '${projectId}'`, {cause: error});
    } finally {
      setFetched(true);
    }
  };


  const pushProject: ProjectPushMethod = async updatedProject => {
    updatedProject = {...project, ...updatedProject};

    try {
      if(!updatedProject) throw new Error("No project to push");
      if(!updatedProject.name) throw new Error("Missing required parameters 'name' for project.");

      const {name, hidden, gameEngine, numMonthlyUsers, platform, newThumbnailFile} = updatedProject;
      const params: Partial<ProjectPushPayloadType & {newThumbnailFile: File}> = {
        gameEngine, hidden, name, numMonthlyUsers, platform
      };

      let newProject;
      if(projectId) {
        newProject = await updateProject(params, projectId);
        clearCache("getProject", [projectId]);
      } else
        newProject = await createProject(params as ProjectPayloadType);
      if(!newProject) throw new Error("Project not pushed");
      projectId = newProject?.id;
      if(config) await updateConfig();
      if(newThumbnailFile || thumbnail)
        await pushThumbnail(newThumbnailFile || thumbnail);
      setProject(newProject);
      return newProject;
    } catch (error) {
      console.error(`Error pushing project ${project?.id || ""}`, error);
      throw new Error(`Error pushing project ${project?.id || ""}`, {cause: error});
    }
  };

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  return {
    ...project,
    fetched,
    project,
    push: pushProject,
    pushNewOwner,
    pushThumbnail,
    refresh: fetchProject,
    setConfig,
    setThumbnail,
    updateConfig
  };
};
